import { MaterialCommunityIcons } from '@expo/vector-icons';
import {
  MediaItem,
  MediaType,
  Product,
  useCreateMutation,
  useCurrentWorkspace,
  useWorkspaceCategories,
} from '@gripp/shared-logic';
import {
  AddLink,
  CategorySelector,
  Colors,
  ControlledTextInput,
  SaveFormTop,
  Spacing,
  TopModalBar,
  useAutoFocusRef,
} from '@gripp/shared-ui';
import { yupResolver } from '@hookform/resolvers/yup';
import { useEffect, useRef, useState } from 'react';
import { useForm } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import { Pressable, StyleSheet, Text, View } from 'react-native';

import * as yup from 'yup';
import { MediaPicker } from '../../shared/components/media/mediaPicker';
import { useMediaUpload } from '../../shared/hooks/useMediaUpload';

const CREATE_MEDIA = `
  mutation createMedia($input: CreateMediaInput!) {
    createMedia(input: $input) {
      id
    }
  }
`;

const CREATE_CATEGORY = `
  mutation createCategory($input: CreateCategoryInput!) {
    createCategory(input: $input) {
      id
      name
    }
  }
`;

type Category = {
  id: string;
  name: string;
};

type Props = {
  isProductContext?: boolean;
  product?: Product;
  onComplete: () => void;
  onProductUploadComplete?: (
    id: string,
    title?: string,
    localUrl?: string
  ) => void;
  type?: 'file' | 'link';
};

export const CreateFileForm = ({
  isProductContext,
  product,
  onComplete,
  onProductUploadComplete,
  type = 'file',
}: Props) => {
  const { t } = useTranslation();
  const currentWorkspace = useCurrentWorkspace();
  const { mutateAsync: createMedia, isPending: isCreatingMedia } =
    useCreateMutation({
      modelName: 'media',
      query: CREATE_MEDIA,
    });

  const { mutateAsync: createCategory, isPending: isCreatingCategory } =
    useCreateMutation({
      modelName: 'category',
      query: CREATE_CATEGORY,
    });

  const [selectedFile, setSelectedFile] = useState<File | null>(null);

  const localUrlRef = useRef<string | null>(null);

  const [errorMessage, setErrorMessage] = useState<string | null>(null);
  const [isUploading, setIsUploading] = useState(false);

  const handleFileSelected = (file: File) => {
    setSelectedFile(file);
    setErrorMessage(null);
    setValue('title', file.name, { shouldDirty: true, shouldValidate: true });
    // Create object URL immediately for thumbnail display
    if (localUrlRef.current) {
      URL.revokeObjectURL(localUrlRef.current);
    }
    const url = URL.createObjectURL(file);
    localUrlRef.current = url;
  };

  useEffect(() => {
    if (!selectedFile) return;
    const url = URL.createObjectURL(selectedFile);
    localUrlRef.current = url;
    return () => {
      if (localUrlRef.current) {
        URL.revokeObjectURL(localUrlRef.current);
        localUrlRef.current = null;
      }
    };
  }, []);

  const { categories } = useWorkspaceCategories();

  const formSchema = yup.object({
    title: yup.string().required(t('file.create.validation.title_required')),
    categories: yup
      .array()
      .of(
        yup.object({
          id: yup.string().required(),
          name: yup.string().required(),
        })
      )
      .optional(),
    linkUrl: yup.string().when('type', {
      is: 'link',
      then: (schema) =>
        schema
          .required(t('file.create.validation.link_required'))
          .url(t('file.create.validation.link_invalid')),
      otherwise: (schema) => schema.optional(),
    }),
  });

  const {
    control,
    handleSubmit,
    formState: { isDirty },
    watch,
    setValue,
  } = useForm({
    resolver: yupResolver(formSchema),
    mode: 'onChange',
    defaultValues: {
      title: isProductContext ? '' : product?.name || '',
      categories: [],
      linkUrl: '',
    },
  });

  const title = watch('title');
  const selectedCategories = watch('categories') || [];
  const linkUrl = watch('linkUrl');
  const canSubmit =
    Boolean(title) &&
    (type === 'link' ? Boolean(linkUrl) : Boolean(selectedFile));

  // Media upload hook
  const uploadMedia = useMediaUpload({
    onStart: (mediaItem: MediaItem) => {
      setIsUploading(true);
    },
    onComplete: (mediaId: string) => {
      setIsUploading(false);
    },
  });

  const handleFileRemove = () => {
    // Clear the preview URL when removing file

    if (localUrlRef.current) {
      URL.revokeObjectURL(localUrlRef.current);
      localUrlRef.current = null;
    }
    setSelectedFile(null);
    setValue('title', '', { shouldDirty: true, shouldValidate: true });
  };

  const handleCreateCategory = async (name: string) => {
    try {
      const result = await createCategory({
        input: {
          name: name.trim(),
          workspace: { id: currentWorkspace.id },
        },
      });

      if (result?.id) {
        return {
          id: result.id,
          name: result.name,
        };
      }
      throw new Error('Failed to create category');
    } catch (error) {
      console.error('Failed to create category:', error);
      throw error;
    }
  };

  const onSubmit = async (data: yup.InferType<typeof formSchema>) => {
    if (!currentWorkspace?.id) return;

    if (type === 'link') {
      if (!data.linkUrl) return;
      try {
        // Create link media record
        const result = await createMedia({
          input: {
            name: data.title,
            type: MediaType.Link,
            extension: 'link',
            status: 'PROCESSED',
            url: data.linkUrl,
            workspaces: [{ id: currentWorkspace.id }],
            categories: (data.categories || []).map((category: Category) => ({
              id: category.id,
            })),
          },
        });

        if (result?.id) {
          if (product) {
            onProductUploadComplete?.(result.id, data.title);
          } else {
            onProductUploadComplete?.(result.id);
          }
        }

        onComplete();
      } catch (error) {
        console.error('Failed to create link:', error);
        setErrorMessage(t('file.create.linkError'));
      }
    } else {
      if (!selectedFile) return;
      try {
        // Upload the file and create the media record with title and categories
        const uploadedMedia = await uploadMedia(
          selectedFile,
          currentWorkspace.id,
          data.title,
          (data.categories || []).map((category: Category) => ({
            id: category.id,
          }))
        );
        setErrorMessage(null);
        if (uploadedMedia) {
          if (product) {
            onProductUploadComplete?.(
              uploadedMedia,
              data.title,
              localUrlRef.current || undefined
            );
          } else {
            onProductUploadComplete?.(
              uploadedMedia,
              undefined,
              localUrlRef.current || undefined
            );
          }
        }

        onComplete();
      } catch (error) {
        console.error('Failed to create file:', error);
        setErrorMessage(t('file.create.uploadError'));
      }
    }
  };

  const elementFocusCallBack = useAutoFocusRef();

  const isPending = isCreatingMedia || isCreatingCategory || isUploading;

  return (
    <View style={styles.contentContainer}>
      <SaveFormTop
        isDirty={isDirty && !isPending}
        isValid={Boolean(canSubmit) && !isPending}
        onSubmit={handleSubmit(onSubmit)}
        title={
          product && isProductContext
            ? product.name
            : type === 'link'
            ? t('file.create.linkLabel')
            : t('file.create.label')
        }
        saveLabel={
          onProductUploadComplete
            ? t('product.detail.save')
            : t('file.create.add')
        }
        topBar={TopModalBar}
        onCancel={onComplete}
      >
        {errorMessage && (
          <View style={{ marginHorizontal: 24, marginBottom: 8 }}>
            <Text style={{ color: Colors.red, fontSize: 14 }}>
              {errorMessage}
            </Text>
          </View>
        )}
        <View style={styles.formItem}>
          <Text style={styles.formItemTitle}>{t('file.create.title')}</Text>
          <ControlledTextInput
            control={control}
            name="title"
            placeholder={t('file.create.titlePlaceholder')}
            innerRef={elementFocusCallBack}
          />
        </View>

        <View style={styles.formItem}>
          <CategorySelector
            selectedCategories={selectedCategories}
            availableCategories={categories}
            onCategoriesChange={(newCategories) => {
              setValue('categories', newCategories, {
                shouldDirty: true,
                shouldValidate: true,
              });
            }}
            onCreateCategory={handleCreateCategory}
          />
        </View>

        {type === 'link' && (
          <View style={styles.formItem}>
            <AddLink name="linkUrl" control={control} />
          </View>
        )}

        {type === 'file' && (
          <View style={styles.formItem}>
            <Text style={styles.formItemTitle}>{t('file.create.file')}</Text>
            {!selectedFile ? (
              <MediaPicker
                autoUpload={false}
                autoTrigger={true}
                onFileSelected={handleFileSelected}
                setMedia={() => {}}
                setProgress={() => {}}
                setUploading={setIsUploading}
                acceptedMediaTypes={[
                  MediaType.Image,
                  MediaType.Document,
                  MediaType.Video,
                ]}
                workspaceId={currentWorkspace?.id}
                customTrigger={
                  <View style={styles.fileUploadArea}>
                    <View style={styles.uploadIconContainer}>
                      <MaterialCommunityIcons
                        name="plus"
                        size={24}
                        color={Colors.blackText}
                      />
                    </View>
                  </View>
                }
              />
            ) : (
              <View style={styles.filePreview}>
                <View style={styles.fileImageContainer}>
                  {/* Optionally preview the file if it's an image */}
                  {selectedFile.type.startsWith('image/') &&
                  localUrlRef.current ? (
                    <img
                      src={localUrlRef.current}
                      alt="File preview"
                      style={styles.fileImage}
                    />
                  ) : (
                    <MaterialCommunityIcons
                      name="file-document"
                      size={24}
                      color={Colors.blackText}
                    />
                  )}
                </View>
                <Pressable
                  style={styles.closeButton}
                  onPress={handleFileRemove}
                >
                  <MaterialCommunityIcons
                    name="close-circle"
                    size={20}
                    color={Colors.primary}
                  />
                </Pressable>
              </View>
            )}
          </View>
        )}
      </SaveFormTop>
    </View>
  );
};

const styles = StyleSheet.create({
  contentContainer: {
    width: '100%',
  },
  formItem: {
    paddingHorizontal: Spacing.basePadding.paddingHorizontal,
    paddingTop: Spacing.basePadding.paddingVertical,
    marginBottom: 16,
  },
  formItemTitle: {
    fontSize: 16,
    fontWeight: '700',
    lineHeight: 16,
    marginBottom: 10,
    color: Colors.blackText,
  },
  fileUploadArea: {
    backgroundColor: Colors.white,
    borderRadius: 8,
    borderWidth: 0, // No border
    padding: 16,
    alignItems: 'center',
    justifyContent: 'center',
    width: 80,
    height: 80,
  },
  uploadIconContainer: {
    width: 40,
    height: 40,
    borderRadius: 4,
    backgroundColor: 'transparent', // Transparent background
    alignItems: 'center',
    justifyContent: 'center',
  },
  filePreview: {
    position: 'relative', // For absolute positioning of close button
    backgroundColor: Colors.white,
    borderRadius: 8,
    borderWidth: 0, // No border
    width: 80,
    height: 80,
    overflow: 'hidden',
  },
  fileImageContainer: {
    width: '100%',
    height: '100%',
    alignItems: 'center',
    justifyContent: 'center',
  },
  fileImage: {
    width: '100%',
    height: '100%',
    objectFit: 'cover', // Fill the rectangle completely
  },
  closeButton: {
    position: 'absolute',
    top: 4,
    right: 4,
    zIndex: 1,
    padding: 2,
  },
  textInput: {
    height: 55,
    backgroundColor: Colors.white,
    borderRadius: 8,
  },
});
